import React, { useCallback } from "react";

import { zindexTourOverlay } from "~/css/zindexes";
import { Tooltip, type TooltipProps } from "~/ui";
import Button from "~/ui/button";

import type { TourStep } from "./Tours";

import styles from "./Tourtip.module.css";

interface TourtipProps {
	step: TourStep;
	index: number;
	size: number;
	targetRef: React.RefObject<HTMLElement>;
	advanceTour: () => void;
}

export default function Tourtip({ step, index, size, targetRef, advanceTour }: TourtipProps) {
	const { tooltipTitle, tooltipContent, interactionTarget } = step;
	const isLastStep = index === size - 1;

	const renderTooltip = useCallback<NonNullable<TooltipProps["render"]>>(
		attrs => (
			<div {...attrs} data-tour-tooltip="" className={styles.tourtipBody}>
				<div className={styles.tourtipContent}>
					<h4 className={styles.tourtipTitle}>{tooltipTitle}</h4>
					<p>{tooltipContent}</p>
				</div>
				<div className={styles.tourtipControls}>
					<span style={{ visibility: size > 1 ? "visible" : "hidden" }} className={styles.tourtipStepCount}>
						{index + 1} of {size}
					</span>
					{!interactionTarget && (
						<Button variant="primary" size="slim" className={styles.tourtipButton} onClick={advanceTour}>
							{isLastStep ? "Got it" : "Next"}
						</Button>
					)}
				</div>
				{!step.showSpotlight && (
					<div data-popper-arrow className={styles.tourtipArrow}>
						<svg viewBox="0 0 20 8" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								// eslint-disable-next-line max-len
								d="M20 8C18.9689 8 17.9913 7.54784 17.3322 6.76616L12.6678 1.23384C11.2807 -0.411279 8.71929 -0.411281 7.33223 1.23384L2.66777 6.76616C2.00871 7.54785 1.03107 8 0 8L20 8Z"
								fill="currentColor"
							/>
						</svg>
					</div>
				)}
			</div>
		),
		[tooltipTitle, tooltipContent, index, size, interactionTarget, advanceTour, isLastStep, step.showSpotlight],
	);

	return (
		<Tooltip
			placement={step.tooltipPlacement}
			visible
			interactive
			render={renderTooltip}
			reference={targetRef}
			appendTo={document.body}
			zIndex={zindexTourOverlay + 1}
			popperOptions={{
				modifiers: [{ name: "offset", options: { offset: [0, step.showSpotlight ? -4 : 8] } }],
			}}
		/>
	);
}
